import { sanitizeAdoHtml } from "@/lib/ado-html";

/**
 * Split a bug's Repro Steps HTML into what ADO shows as separate parts
 * (UX-PM-44, feature PI-SPEC-SW-1471): the numbered steps, and the labelled
 * paragraphs the API appends after them.
 *
 * The API writes Repro Steps as `<ol><li>…</li></ol>` followed by
 * `<p><strong>Expected:</strong> …</p>`, `Actual` and an optional
 * `Frequency` paragraph (packages/api/src/services/interview/interviewEngine.ts
 * `renderSections`). The Formulator review shows the steps as a numbered list
 * and each labelled paragraph as its own row, so the person sees what ADO
 * will show before Create in ADO.
 *
 * The input is sanitised first, so every returned HTML string is sanitised
 * HTML; `labelled[].text` is plain text, for rendering as React text.
 */

export interface LabelledReproRow {
  /** The paragraph's bold label without its colon, e.g. "Expected". */
  label: string;
  /** The rest of the paragraph as plain text, trimmed. */
  text: string;
}

export interface SplitReproSteps {
  /** The first top-level `ol` (or `ul`) as sanitised HTML; "" when there is none. */
  stepsHtml: string;
  /** Each top-level `<p><strong>Label:</strong> text</p>`, in order. */
  labelled: LabelledReproRow[];
  /** Everything else, in order, as sanitised HTML; "" when nothing is left. */
  rest: string;
}

/** A paragraph that opens with a bold `Label:`; null for any other node. Pure. */
function labelledParagraph(node: ChildNode): LabelledReproRow | null {
  if (!(node instanceof HTMLParagraphElement)) return null;
  const first = [...node.childNodes].find((child) => !(child.nodeType === Node.TEXT_NODE && (child.textContent ?? "").trim() === ""));
  if (!(first instanceof HTMLElement) || !["STRONG", "B"].includes(first.tagName)) return null;
  const heading = first.textContent.trim();
  if (!heading.endsWith(":")) return null;
  const label = heading.slice(0, -1).trim();
  if (label === "") return null;
  const text = [...node.childNodes]
    .filter((child) => child !== first)
    .map((child) => child.textContent ?? "")
    .join("")
    .trim();
  return { label, text };
}

/** Split Repro Steps HTML into its steps list, labelled rows and the rest. Pure; exported for tests. */
export function splitReproSteps(html: string): SplitReproSteps {
  const clean = sanitizeAdoHtml(html);
  if (clean === null) return { stepsHtml: "", labelled: [], rest: "" };

  const doc = new DOMParser().parseFromString(clean, "text/html");
  const rest = doc.createElement("div");
  const labelled: LabelledReproRow[] = [];
  let stepsHtml = "";

  for (const node of [...doc.body.childNodes]) {
    if (stepsHtml === "" && (node instanceof HTMLOListElement || node instanceof HTMLUListElement)) {
      stepsHtml = node.outerHTML;
      continue;
    }
    const row = labelledParagraph(node);
    if (row) {
      labelled.push(row);
      continue;
    }
    rest.appendChild(node);
  }

  return { stepsHtml, labelled, rest: rest.textContent.trim() === "" ? "" : rest.innerHTML };
}
